/**
 * API service for labels management
 */
import axios from 'axios';
import { Label, LabelCreateRequest, LabelUpdateRequest, LabelsByCategory } from '../types/labels';
import { API_BASE_URL } from '../utils/apiConfig';
import { PaginatedResponse } from '../types/api';

// Re-export Label type for convenience
export type { Label } from '../types/labels';

export interface GetLabelsParams {
  category?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export const labelsApi = {
  /**
   * Get list of labels with optional filtering
   */
  async getLabels(params?: GetLabelsParams): Promise<PaginatedResponse<Label>> {
    const response = await axios.get(`${API_BASE_URL}/labels/`, { params });
    // Handle paginated response
    if (Array.isArray(response.data)) {
      return {
        results: response.data,
        count: response.data.length,
        next: null,
        previous: null,
      };
    }
    return {
      results: response.data.results || [],
      count: response.data.count || 0,
      next: response.data.next || null,
      previous: response.data.previous || null,
    };
  },

  /**
   * Get single label by ID
   */
  async getLabel(id: number): Promise<Label> {
    const response = await axios.get(`${API_BASE_URL}/labels/${id}/`);
    return response.data;
  },

  /**
   * Create a new label
   */
  async createLabel(data: LabelCreateRequest): Promise<Label> {
    const response = await axios.post(`${API_BASE_URL}/labels/`, data);
    return response.data;
  },

  /**
   * Update an existing label
   */
  async updateLabel(id: number, data: LabelUpdateRequest): Promise<Label> {
    const response = await axios.patch(`${API_BASE_URL}/labels/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a label
   */
  async deleteLabel(id: number): Promise<void> {
    await axios.delete(`${API_BASE_URL}/labels/${id}/`);
  },

  /**
   * Get labels grouped by category
   */
  async getLabelsByCategory(): Promise<LabelsByCategory> {
    const response = await axios.get(`${API_BASE_URL}/labels/by_category/`);
    return response.data;
  },
};
